import { For, Show, createMemo, type Component } from 'solid-js';
import { Text } from '~ui/text';
import { formatCurrency, formatPercent } from '~/lib/format';
import { accountExposures, coveredFunds, loanExposures, type Axis, type Exposure } from '~/stores/exposure-store';

// What this account is actually made of, under the positions that make it.
//
// The table above says you own HMAX and HDIV. It does not say that both of them
// are most of the way into the same five banks, which is the thing about a
// two-fund account that is worth knowing and the thing neither row admits to.
//
// Three axes side by side rather than behind tabs, for the same reason the
// payments page puts its three views next to each other: they are read against
// each other. "Financials 62%" is a fact; "Financials 62%, Canada 94%" is a
// position. A tab strip would make holding both in mind a memory exercise.
//
// Shares are against what resolved inside *this account*, not against the book.
// The question on this screen is what this account is made of.

const AXES: { axis: Axis; title: string }[] = [
    { axis: 'company', title: 'Companies' },
    { axis: 'sector', title: 'Sectors' },
    { axis: 'geography', title: 'Regions' },
];

const Column: Component<{ rows: Exposure[]; axis: Axis; title: string }> = (props) => {
    const rows = createMemo(() => props.rows);

    // "Nothing here" and "nothing could be resolved" are different answers, and
    // only one of them means the account is fine. Same distinction the Exposure
    // page draws — a look-through that resolved none of your funds still renders
    // an empty list, which reads as "no exposure".
    const empty = createMemo(() =>
        coveredFunds(props.axis).length === 0
            ? props.axis === 'company'
                ? 'No fund here publishes its holdings'
                : `No fund here publishes a ${props.axis === 'sector' ? 'sector' : 'geographic'} breakdown`
            : 'Nothing to resolve',
    );

    return (
        <div class='flex min-h-0 min-w-0 flex-1 flex-col bg-background'>
            <div class='flex h-row shrink-0 items-center justify-between gap-2 border-b px-2 whitespace-nowrap'>
                <Text weight='medium'>{props.title}</Text>
                <Show when={rows().length > 0}>
                    <Text tone='muted'>{rows().length}</Text>
                </Show>
            </div>

            <div class='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
                <Show when={rows().length > 0} fallback={<Text tone='muted' class='block px-2 py-3'>{empty()}</Text>}>
                    <For each={rows()}>
                        {(e) => (
                            // Dollars on the title rather than in a third column:
                            // at a third of the pane there is room for a name and
                            // one number, and the share is the one that compares
                            // across the three columns.
                            <div class='flex h-row items-center justify-between gap-2 border-b px-2 whitespace-nowrap' title={`${e.name} · ${formatCurrency(e.value)}`}>
                                <Text class='min-w-0 truncate'>{e.ticker ?? e.name}</Text>
                                <Text tone='muted'>{formatPercent(e.share, 1)}</Text>
                            </div>
                        )}
                    </For>
                </Show>
            </div>
        </div>
    );
};

/** Given a resolver rather than an id, so the Loans page draws the same three
    columns off its own scope — the borrowed share of each position — without a
    second copy of the layout. */
const Columns: Component<{ resolve: (axis: Axis) => Exposure[] }> = (props) => (
    <div class='flex min-h-0 flex-1 gap-px bg-border'>
        <For each={AXES}>{(a) => <Column rows={props.resolve(a.axis)} axis={a.axis} title={a.title} />}</For>
    </div>
);

export const AccountExposure: Component<{ accountId: string }> = (props) => <Columns resolve={(axis) => accountExposures(props.accountId, axis)} />;

export const LoanExposure: Component<{ loanId: string }> = (props) => <Columns resolve={(axis) => loanExposures(props.loanId, axis)} />;

export default AccountExposure;
